import { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useProductListContext } from '../../../utilities/context/static/ProductListContext';
import { HtmlText } from '../../unmanaged/templates/HtmlText';
import type { Button } from '~/amplienceTypes/schemas/exported/button-schema';
import { CategoryTag } from '~/components/plp/categoryTag/CategoryTag';
import { media } from '~/components/shared/core/styles';
import { ColorScheme } from '~/components/unmanaged/styles/BaseStyledModifiers';
import { BoxSizingBorder } from '~/components/unmanaged/styles/BaseStyledStyling';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';
import { debounce } from '~/utilities/parsers';

const S = {
  Container: styled.div`
    ${BoxSizingBorder}
    --ds-color-content-primary: ${({ theme }) => theme.colors.content.primary};
    --ds-color-content-secondary: ${({ theme }) => theme.colors.content.secondary};
    --padding-inline: var(--ds-spacing-md, 24px);

    margin-inline: auto;
    padding: var(--ds-spacing-xl, 40px) var(--padding-inline) var(--ds-spacing-2xl, 64px);

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      max-width: calc(var(--footer-max-width, 1656px) + var(--padding-inline) * 2);
    }

    @media ${({ theme }) => media(theme).greaterThan('desktop-lg')} {
      --padding-inline: 48px;
    }
  `,
  Content: styled.div<{ $clamp: boolean; $clampLines: number }>`
    text-align: left;

    .contentBlock-bodyAlign {
      transition: height 0.5s cubic-bezier(0.78, 0, 0.22, 1);
      overflow: hidden;
      height: var(--height, auto);

      @media (prefers-reduced-motion) {
        transition: none;
      }

      .contentBlock-body-text {
        color: var(--ds-color-content-secondary);
        font-size: var(--ds-body-sm, 14px);
        line-height: 1.4em;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${({ $clamp, $clampLines }) => ($clamp ? $clampLines : 'none')};
        overflow: hidden;

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          margin-block-end: var(--ds-spacing-3xs, 4px);
          font-size: var(--ds-body-md, 16px);
          font-weight: 500;
          line-height: inherit;
        }

        p {
          margin-block-end: var(--ds-spacing-md, 24px);
        }

        a {
          text-decoration: underline;
          text-decoration-style: dotted;
          text-decoration-color: currentcolor;
          text-decoration-thickness: 1px;
          text-underline-offset: 2px;

          &:hover,
          &:focus {
            color: var(--ds-color-content-primary);
            text-decoration-style: solid;
          }
        }
      }
    }
  `,
  // TODO: Replace with Design System Button when available
  ToggleButton: styled.button`
    margin-block-start: var(--ds-spacing-3xs, 4px);
    color: var(--ds-color-content-secondary);
    font-size: var(--size-body-sm, 14px);
    font-style: normal;
    font-weight: 500;
    line-height: 1.4em;
    text-transform: capitalize;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-decoration-color: currentcolor;
    text-underline-offset: 4px;
    outline-offset: 3px;
  `,

  Tags: styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-block-start: var(--ds-spacing-xl, 40px);
    gap: var(--ds-spacing-3xs, 4px);
    justify-content: flex-start;

    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        [SiteType.Outlet]: ColorScheme.light,
      })}
  `,
};

export const SeoText = () => {
  const { pageContent, systemTexts } = useProductListContext();
  const [width, setWidth] = useState<number>();
  const ref = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const clampedHeight = useRef<number>(null);

  const { showMoreBtnText = '', showLessBtnText = '' } = systemTexts?.seo ?? {};

  const clampLines = 4;

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showToggle, setShowToggle] = useState(false);

  const prevIsCollapsed = useRef<boolean>(true);

  useLayoutEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('.js-contentBlock-body-text');

    if (!el) {
      return () => {};
    }

    const handleResize = (entries: ResizeObserverEntry[]) => {
      const rect = entries[0].contentRect;

      setWidth(rect.width);
    };

    const debouncedResize = debounce(handleResize, 100);
    const observer = new ResizeObserver(debouncedResize);

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('.js-contentBlock-body-text');

    if (!el) {
      setShowToggle(false);

      return;
    }

    el.style.webkitLineClamp = String(clampLines);
    el.style.overflow = 'hidden';

    const hasClamp = el.scrollHeight > el.clientHeight;

    clampedHeight.current = el.clientHeight;

    setShowToggle(hasClamp);

    if (!initialized.current) {
      setIsCollapsed(hasClamp);
      initialized.current = true;
    }

    el.style.webkitLineClamp = '';
    el.style.overflow = '';
  }, [pageContent?.seo, width]);

  useLayoutEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('.js-contentBlock-body-text');

    if (!el?.parentElement) {
      return;
    }

    el.parentElement.style.setProperty(
      '--height',
      `${isCollapsed ? clampedHeight.current : el.scrollHeight}px`
    );
    el.style.webkitLineClamp = 'none';

    if (prevIsCollapsed.current !== isCollapsed) {
      el.parentElement.ontransitionend = () => {
        el.style.webkitLineClamp = '';
      };
    } else {
      el.style.webkitLineClamp = '';
    }

    prevIsCollapsed.current = isCollapsed;
  }, [isCollapsed, width]);

  const toggle = () => {
    setIsCollapsed(prev => !prev);
  };

  if (!pageContent?.seo && !pageContent?.seoCategories) {
    return null;
  }

  return (
    <S.Container>
      {pageContent?.seo && (
        <S.Content data-testid="seo-text" $clamp={isCollapsed} $clampLines={clampLines} ref={ref}>
          {pageContent.seo.map((seoContent, idx) => (
            <HtmlText {...seoContent} key={idx} />
          ))}
          {showToggle && (
            <S.ToggleButton
              type="button"
              onClick={toggle}
              aria-expanded={!isCollapsed}
              data-testid={isCollapsed ? 'seo-text-show-more-btn' : 'seo-text-show-less-btn'}
            >
              {String(isCollapsed ? showMoreBtnText : showLessBtnText)}
            </S.ToggleButton>
          )}
        </S.Content>
      )}
      {pageContent?.seoCategories && (
        <S.Tags>
          {pageContent.seoCategories.map(({ button }: Button, idx) => (
            <CategoryTag {...button} key={`${button?.url}-${idx}`} />
          ))}
        </S.Tags>
      )}
    </S.Container>
  );
};
